import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Input from 'components/common/Input';
import { Button, Card, Collapse, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import Flex from 'components/common/Flex';
import { changeToCoin, changeToNumber } from 'helpers/utils';
import Background from 'components/common/Background';
import bg2 from 'assets/img/icons/spot-illustrations/corner-3.png';
import classNames from 'classnames';
import useScreenSize from 'hooks/useScreenSize';
import SimpleBarReact from 'simplebar-react';

const customStyles = {
 groupHeading: base => ({
  ...base,
  textAlign: 'right', // Alinear el texto a la derecha
  color: '#ff6347' // Cambiar el color del texto (puedes ajustar el color según tus preferencias)
  //   backgroundColor: '#f2f2f2'
 })
};

const defaultValues = { price: '', references: [] };

const PricingReference = ({ price, options, append, update, setOpen }) => {
 const {
  reset,
  watch,
  setValue,
  register,
  handleSubmit,
  formState: { errors }
 } = useForm({ defaultValues });

 const pric = watch('price');
 const references = watch('references');

 const [select, setSelect] = useState([]);
 const [amount, setAmount] = useState('');

 const onChange = ({ target: { value } }) =>
  setValue('price', changeToNumber({ amount: value, cents: true }), { shouldValidate: true });

 useEffect(() => {
  setAmount(changeToCoin({ amount: pric, symbol: true, cents: true }));
 }, [pric]);

 useEffect(() => {
  setSelect(options.flatMap(group => group.options).filter(a => references.some(u => u === a.value)) ?? []);
 }, [references]);

 useEffect(() => {
  reset({ ...defaultValues, ...price });
 }, [price]);

 const onSubmitData = newD => {
  const { index, ...newData } = newD;

  if (index >= 0) update(index, newData);
  else append(newData);

  reset(defaultValues);

  return setOpen(false);
 };

 const onError = err => console.log('se genero un error en el formulario', err);

 return (
  <Row className="gx-1">
   <Input
    errors={errors}
    type="selectMulti"
    name="references"
    placeholder="Seleccione ref..."
    options={options}
    formGroupProps={{ className: 'mb-1 mb-md-0 col-12 col-md-8' }}
    formControlProps={{
     styles: customStyles,
     isClearable: true,
     isMulti: true,
     closeMenuOnSelect: true,
     value: select,
     onChange: newValue => {
      const values = newValue.map(e => e.value);
      setValue('references', values, { shouldValidate: true });
     }
    }}
   />

   <Input
    type="hidden"
    formControlProps={{ ...register('references', { required: 'Debe seleccionar al menos 1 referencia' }) }}
   />

   <Flex direction="row" className="col-12 col-md-4" justifyContent="start" alignItems="center" wrap="nowrap">
    <Input
     type="text"
     name="price"
     errors={errors}
     placeholder="Precio Ref"
     formGroupProps={{ className: 'borders ps-0 ps-md-1 pe-2 flex-grow-1 border-end border-end-2' }}
     formControlProps={{
      className: 'text-center',
      onFocus: e => e.target.select(),
      //   plaintext: true,
      value: amount,
      onChange
     }}
    />

    <Input
     type="hidden"
     formControlProps={{
      ...register('price', {
       required: 'Campo requerido',
       validate: value => (!isNaN(value) && parseFloat(value) > 0) || 'Debe ser > 0'
      })
     }}
    />

    <Button
     variant="falcon-success"
     type="button"
     className="icon-item icon-item-sm ms-3"
     onClick={() => handleSubmit(onSubmitData, onError)()}
    >
     <FontAwesomeIcon className="fs-0" icon="plus" />
    </Button>
   </Flex>
  </Row>
 );
};

const ProductPricingRef = ({ article }) => {
 const { width } = useScreenSize();
 const { control, setValue, getValues } = useFormContext();
 const { fields, append, update, remove } = useFieldArray({ control, name: 'pricingReference' });

 const [code, setCode] = useState(getValues('article') ?? null);
 const [options, setOptions] = useState([]);
 const [open, setOpen] = useState(false);
 const [price, setPrice] = useState({});

 useEffect(() => {
  const optionsGroups = article?.references.map(e => ({
   label: e.reference,
   options: e.properties.map(a => ({ value: a._id, label: a.name }))
  }));

  setOptions(optionsGroups ?? []);
  const articleCode = getValues('article');
  if (articleCode !== code) {
   setCode(articleCode);
   setValue('pricingReference', []);
  }
 }, [article]);

 useEffect(() => {
  if (!open) setPrice({});
 }, [open]);

 const regroupRef = references => {
  return options.reduce((filteredGroups, group) => {
   const options = group.options.filter(option => references.some(a => a === option.value)).map(e => e.label);
   if (options.length) filteredGroups.push({ ...group, options });
   return filteredGroups;
  }, []);
 };

 return (
  <Card className="mb-3">
   <Card.Body>
    <Flex className="g-2" direction="row" justifyContent="start" alignItems="center" wrap="nowrap">
     <div className="flex-grow-1 pe-2 border-end border-end-2">
      <OverlayTrigger
       overlay={
        <Tooltip style={{ position: 'fixed' }} id="finalPriceTooltip">
         Product final price
        </Tooltip>
       }
      >
       <span className="d-flex text-primary fs-0">
        <FontAwesomeIcon icon="question-circle" />
        <h5 className="ms-1 mb-0 fs-0">Precios & Referencias</h5>
       </span>
      </OverlayTrigger>

      <p className="m-0 fs--2 text-muted">Crea nuevas referencias de precio</p>
     </div>

     <Button variant="falcon-primary" type="button" className="icon-item icon-item-sm ms-3" onClick={() => setOpen(!open)}>
      <FontAwesomeIcon className="" icon={open ? 'chevron-up' : 'chevron-down'} />
     </Button>
    </Flex>

    {fields.length ? (
     <SimpleBarReact className="mx-1" forceVisible="x" autoHide={true} style={{ whiteSpace: 'nowrap', overflowX: 'auto' }}>
      <Flex alignItems="stretch" alignContent="end" className="mb-2">
       {fields.map((ref, index) => {
        const { references, price } = ref;
        const refs = regroupRef(references);
        return (
         <Card
          key={index}
          className={classNames('bg-transparent border mt-3 flex-fill', { 'ms-2': index })}
          style={{ minWidth: '10rem' }}
         >
          <Background image={bg2} className="bg-card" />
          <Card.Body className="hover-actions-trigger fs--1 p-3 pb-2">
           <div className="d-flex flex-column align-items-start">
            <div className="d-flex align-items-center">
             <h6 className="mb-0 me-3 d-none d-md-block">Precio:</h6>
             <h6 className="mb-0 fs-0 display-6">{changeToCoin({ amount: price, symbol: true, cents: true })}</h6>
            </div>
            <h6 className="my-1 text-primary fs--2">Referencias</h6>
           </div>

           <SimpleBarReact className="m-0" forceVisible="x" autoHide={true} style={{ whiteSpace: 'nowrap', overflowX: 'auto' }}>
            <Flex className="mb-2">
             {refs.map((ref, i) => (
              <div key={i} className="px-2 flex-fill">
               <h6 className="text-700 fs--2 mb-0">{ref?.label}:</h6>

               <ul className="fa-ul ms-2 ps-x1 mb-0 mt-1">
                {ref?.options.map((e, i) => (
                 <li className="py-0" key={i}>
                  <h6 className="text-700 m-0 fs--2">
                   <FontAwesomeIcon icon="check" className="fa-li text-success" />
                   {e}
                  </h6>
                 </li>
                ))}
               </ul>
              </div>
             ))}
            </Flex>
           </SimpleBarReact>

           <div className={classNames('end-0 top-0 p-1 p-md-0 hover-actions', { 'translate-middle-y': width > 425 })}>
            <Button
             variant="link"
             type="button"
             className="d-flex align-items-center fs--1 text-700 p-0"
             onClick={() => {
              setOpen(true);
              setPrice({ ...ref, index });
             }}
            >
             <FontAwesomeIcon icon="pencil-alt" />
            </Button>

            <Button
             size="sm"
             variant="link"
             type="button"
             className="d-flex align-items-center fs-1 text-700 p-0 ms-3"
             onClick={() => remove(index)}
            >
             <FontAwesomeIcon icon="times-circle" />
            </Button>
           </div>
          </Card.Body>
         </Card>
        );
       })}
      </Flex>
     </SimpleBarReact>
    ) : (
     <></>
    )}

    <Collapse in={open}>
     <div className="mt-3">
      <PricingReference price={price} options={options} append={append} update={update} setOpen={setOpen} />
     </div>
    </Collapse>
   </Card.Body>
  </Card>
 );
};

ProductPricingRef.propTypes = {
 article: PropTypes.object
};

PricingReference.propTypes = {
 price: PropTypes.object,
 options: PropTypes.array,
 append: PropTypes.func,
 update: PropTypes.func,
 setOpen: PropTypes.func
};

export default ProductPricingRef;
